import type { FimAssetInstanceFill } from 'features/fim'
import { collectDocumentRefs } from 'features/fim/assetInventory'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'

import type { SubjectCitation } from './citations'

/** One document in a Subject's reference list — what the preview panel steps through. */
export interface SubjectReference {
  documentId: string
  label: string
  /** Page to preview and to frame an open on, when the reference names one. */
  page?: number
  /** Every page this document is cited on. */
  pages: number[]
  /** Cited region on `page`, in normalized page fractions. */
  boxes?: NormalizedBBox[]
  reason?: string
}

/**
 * A Subject's referenced documents in preview order: its citations first, then
 * the documents its Asset Instance fill bound, deduped by document id. A
 * citation wins the dedupe because it carries a page and a region; a bound
 * document slot carries neither.
 */
export const buildSubjectReferenceList = (
  citations: readonly SubjectCitation[],
  fill: FimAssetInstanceFill | null | undefined
): SubjectReference[] => {
  const byId = new Map<string, SubjectReference>()

  for (const citation of citations) {
    if (byId.has(citation.documentId)) continue
    byId.set(citation.documentId, {
      documentId: citation.documentId,
      label: citation.label,
      page: citation.region?.page ?? citation.pages[0],
      pages: citation.pages,
      boxes: citation.region?.boxes,
      reason: citation.reason,
    })
  }

  for (const ref of collectDocumentRefs(fill)) {
    if (byId.has(ref.doc_id)) continue
    byId.set(ref.doc_id, {
      documentId: ref.doc_id,
      label: ref.title ?? ref.doc_id,
      pages: [],
    })
  }

  return [...byId.values()]
}

/** Position of a document in the reference list, or -1 when it is absent. */
export const subjectReferenceIndex = (
  references: readonly SubjectReference[],
  documentId: string | null
): number =>
  documentId === null
    ? -1
    : references.findIndex((reference) => reference.documentId === documentId)
